import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { Link } from 'react-router-dom'
import { useToast } from '../hooks/useToast'

// Helper para formatear como pesos chilenos sin decimales
const formatoPesosChilenos = (valor) => {
  if (!valor && valor !== 0) return ''
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(valor)
}

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalClientes: 0,
    totalServicios: 0,
    ingresosPendientes: 0,
    serviciosActivos: 0
  })
  const [ultimosClientes, setUltimosClientes] = useState([])
  const [proximosServicios, setProximosServicios] = useState([])
  const [loading, setLoading] = useState(true)
  const { addToast } = useToast()

  useEffect(() => {
    cargarDatos()
  }, [])

  const cargarDatos = async () => {
    try {
      const user = (await supabase.auth.getUser()).data.user
      if (!user) return

      // Clientes totales
      const { count: totalClientes, error: errClientes } = await supabase
        .from('clientes')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)

      if (errClientes) throw errClientes

      // Servicios totales y cálculo de ingresos pendientes
      const { data: servicios, count: totalServicios, error: errServicios } = await supabase
        .from('servicios')
        .select('*', { count: 'exact' })
        .eq('user_id', user.id)

      if (errServicios) throw errServicios

      // Ingresos pendientes (servicios en estado "pendiente")
      const pendientes = servicios?.filter(s => s.estado === 'pendiente') || []
      const ingresosPendientes = pendientes.reduce((acc, s) => acc + (parseFloat(s.precio) || 0), 0)

      // Servicios activos (estado "en curso")
      const activos = servicios?.filter(s => s.estado === 'en curso') || []

      // Últimos 5 clientes
      const { data: clientes, error: errUltimos } = await supabase
        .from('clientes')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5)

      if (errUltimos) throw errUltimos

      // Próximos servicios (fecha fin próxima, pendientes o en curso)
      const hoy = new Date().toISOString().split('T')[0]
      const { data: proximos, error: errProximos } = await supabase
        .from('servicios')
        .select('*, clientes(nombre)')
        .eq('user_id', user.id)
        .in('estado', ['pendiente', 'en curso'])
        .gte('fecha_fin', hoy)
        .order('fecha_fin', { ascending: true })
        .limit(5)

      if (errProximos) throw errProximos

      setStats({
        totalClientes: totalClientes || 0,
        totalServicios: totalServicios || 0,
        ingresosPendientes,
        serviciosActivos: activos.length
      })
      setUltimosClientes(clientes || [])
      setProximosServicios(proximos || [])
    } catch (error) {
      addToast('Error al cargar datos del dashboard', 'error')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Cargando dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-semibold text-gray-800 mb-6">Dashboard</h1>

      {/* Tarjetas de resumen */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <p className="text-sm text-gray-500 mb-1">Total Clientes</p>
          <p className="text-2xl font-semibold text-gray-800">{stats.totalClientes}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <p className="text-sm text-gray-500 mb-1">Total Servicios</p>
          <p className="text-2xl font-semibold text-gray-800">{stats.totalServicios}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <p className="text-sm text-gray-500 mb-1">Servicios Activos</p>
          <p className="text-2xl font-semibold text-gray-800">{stats.serviciosActivos}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <p className="text-sm text-gray-500 mb-1">Ingresos Pendientes</p>
          <p className="text-2xl font-semibold text-green-600">
            {formatoPesosChilenos(stats.ingresosPendientes)}
          </p>
        </div>
      </div>

      {/* Contenido de dos columnas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Últimos clientes */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h2 className="text-lg font-medium text-gray-800 mb-4">Últimos clientes</h2>
          {ultimosClientes.length === 0 ? (
            <p className="text-gray-400 text-center py-4">No hay clientes aún</p>
          ) : (
            <ul className="space-y-3">
              {ultimosClientes.map(cliente => (
                <li key={cliente.id}>
                  <Link
                    to={`/clientes/${cliente.id}`}
                    className="block p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <p className="font-medium text-gray-800">{cliente.nombre}</p>
                    <p className="text-sm text-gray-500">{cliente.empresa || '—'}</p>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-4 text-right">
            <Link to="/clientes" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              Ver todos →
            </Link>
          </div>
        </div>

        {/* Próximos servicios */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h2 className="text-lg font-medium text-gray-800 mb-4">Próximos servicios</h2>
          {proximosServicios.length === 0 ? (
            <p className="text-gray-400 text-center py-4">No hay servicios próximos</p>
          ) : (
            <ul className="space-y-3">
              {proximosServicios.map(servicio => (
                <li key={servicio.id} className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-800">{servicio.titulo}</p>
                  <p className="text-sm text-gray-500">
                    Cliente: {servicio.clientes?.nombre} | Fin: {new Date(servicio.fecha_fin).toLocaleDateString('es-CL')}
                  </p>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-4 text-right">
            <Link to="/servicios" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              Ver todos →
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}